import { NumberInput, type NumberInputProps } from '@mantine/core';
import { useState } from 'react';

/**
 * 金額の入力欄。
 *
 * 入力中の値を書き換えない。単位 5 の卓で 140 と打つとき、途中の 14 は範囲外かつ単位の
 * 倍数でもないが、ここで値を丸めたり最小値へ戻したりすると 140 に辿り着けない。
 * そのため次を守る。
 *
 * - `min` / `max` を渡さない（Mantine はこれがあるとクランプする）
 * - 値は文字列のまま親が持つ。空欄や「-」だけの状態も保持する
 * - 妥当性は親が `problem` として渡し、確定ボタンを押せなくするのは親の責務
 * - エラー文はフォーカスが外れているときだけ出す。打っている最中に赤字が点滅すると、
 *   入力を止めるべきだと読まれる
 */
export type AmountValue = string | number;

/** 入力欄の値を整数として読む。読めなければ null（空欄、「-」だけ、小数など）。 */
export function parseAmount(value: AmountValue): number | null {
  if (typeof value === 'number') return Number.isSafeInteger(value) ? value : null;
  const trimmed = value.trim();
  if (!/^-?\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return Number.isSafeInteger(n) ? n : null;
}

type Props = Omit<
  NumberInputProps,
  'value' | 'onChange' | 'error' | 'min' | 'max' | 'clampBehavior' | 'onFocus' | 'onBlur'
> & {
  value: AmountValue;
  onChange: (value: AmountValue) => void;
  /** 確定できない理由。null なら妥当。 */
  problem: string | null;
};

export function AmountInput({ value, onChange, problem, ...rest }: Props): React.ReactElement {
  const [focused, setFocused] = useState(false);
  return (
    <NumberInput
      {...rest}
      value={value}
      onChange={onChange}
      allowDecimal={false}
      // モバイルでテンキーを出す。Mantine の既定は type="text" で、全角キーボードが出ることがある。
      inputMode="numeric"
      error={!focused && problem !== null ? problem : null}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
}
